/**
 * Fleet and Deliveries preview: every route, once.
 *
 * `FD_ROUTE_PATTERNS` holds the 16 route patterns as the router declares them (CO-910 cut seven of
 * the first 30: the vehicle register, its detail page, Trackers, the driver's Clocking and History
 * screens, and the folds of Vehicle History into the Live Map and Retry into Deliver; then the owner
 * cut the seven phone-frame driver pages on 2026-10-28, the driver's screens being the Flutter app.
 * App.tsx redirects each old URL). `fdPaths` builds a
 * concrete URL for each. Pages link through `fdPaths` and never hand-write a path, so a route that
 * moves is changed here and nowhere else.
 */

/** The six delivery reports, in the order the Reports page lists them. The first is the default. */
export const FD_REPORT_KEYS = [
  'driver-trips',
  'delivery-time',
  'assign-collect-deliver',
  'between-return',
  'driver-utilisation',
  'failed-delayed',
] as const;

export type FdReportKey = (typeof FD_REPORT_KEYS)[number];

export const FD_DEFAULT_REPORT_KEY: FdReportKey = FD_REPORT_KEYS[0];

export const isFdReportKey = (value: string | undefined): value is FdReportKey =>
  value !== undefined && (FD_REPORT_KEYS as readonly string[]).includes(value);

/** `?mode=` value that turns the Live Map into its History mode. */
export const FD_MAP_MODE_HISTORY = 'history';

export const FD_ROUTE_PATTERNS = {
  /* Fleet */
  fleetLiveMap: '/fleet/live-map',
  /* Deliveries */
  orders: '/deliveries/orders',
  orderNew: '/deliveries/orders/new',
  orderImport: '/deliveries/orders/import',
  orderDetail: '/deliveries/orders/:orderRef',
  trips: '/deliveries/trips',
  tripNew: '/deliveries/trips/new',
  tripDetail: '/deliveries/trips/:tripId',
  drivers: '/deliveries/drivers',
  alerts: '/deliveries/alerts',
  reports: '/deliveries/reports',
  report: '/deliveries/reports/:reportKey',
  /* Settings > Deliveries & Fleet */
  settingsDelivery: '/settings/deliveries-fleet/delivery',
  settingsCollectionPoints: '/settings/deliveries-fleet/collection-points',
  settingsNotifications: '/settings/deliveries-fleet/notifications',
  settingsAccess: '/settings/deliveries-fleet/access',
} as const;

export type FdRouteName = keyof typeof FD_ROUTE_PATTERNS;

const seg = (value: string): string => encodeURIComponent(value);

/** Appends the query parameters that have a value; none gives the bare path. */
const withQuery = (path: string, params: Record<string, string | undefined>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, value);
  });
  const query = search.toString();
  return query ? `${path}?${query}` : path;
};

export const fdPaths = {
  /* Fleet */
  fleetLiveMap: (): string => FD_ROUTE_PATTERNS.fleetLiveMap,
  /**
   * The Live Map's History mode (CO-910 folded the old /fleet/vehicle-history page into it, as the
   * real /attendance/maps does). Vehicle and trip preselected through `?vehicle=&trip=`.
   */
  fleetVehicleHistory: (vehicleId?: string, tripId?: string): string =>
    withQuery(FD_ROUTE_PATTERNS.fleetLiveMap, { mode: FD_MAP_MODE_HISTORY, vehicle: vehicleId, trip: tripId }),

  /* Deliveries */
  orders: (): string => FD_ROUTE_PATTERNS.orders,
  orderNew: (): string => FD_ROUTE_PATTERNS.orderNew,
  orderImport: (): string => FD_ROUTE_PATTERNS.orderImport,
  order: (orderRef: string): string => `${FD_ROUTE_PATTERNS.orders}/${seg(orderRef)}`,
  trips: (): string => FD_ROUTE_PATTERNS.trips,
  /** Orders ticked on the Orders or Trips page travel in `?orders=4624,4625`. */
  tripNew: (orderRefs: string[] = []): string =>
    withQuery(FD_ROUTE_PATTERNS.tripNew, { orders: orderRefs.length > 0 ? orderRefs.join(',') : undefined }),
  trip: (tripId: string): string => `${FD_ROUTE_PATTERNS.trips}/${seg(tripId)}`,
  drivers: (): string => FD_ROUTE_PATTERNS.drivers,
  alerts: (): string => FD_ROUTE_PATTERNS.alerts,
  reports: (): string => FD_ROUTE_PATTERNS.reports,
  report: (reportKey: FdReportKey): string => `${FD_ROUTE_PATTERNS.reports}/${reportKey}`,

  /* Settings */
  settingsDelivery: (): string => FD_ROUTE_PATTERNS.settingsDelivery,
  settingsCollectionPoints: (): string => FD_ROUTE_PATTERNS.settingsCollectionPoints,
  settingsNotifications: (): string => FD_ROUTE_PATTERNS.settingsNotifications,
  settingsAccess: (): string => FD_ROUTE_PATTERNS.settingsAccess,
};

/** Splits the `?orders=` value written by `fdPaths.tripNew` back into order refs. */
export const parseOrderRefsParam = (value: string | null): string[] =>
  value ? value.split(',').map((ref) => ref.trim()).filter((ref) => ref.length > 0) : [];
